import React from "react";
import { motion } from "framer-motion";
import { Download } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { pillars, counters, IMG } from "../lib/data";
import { Icon } from "../components/Icon";
import Counter from "../components/Counter";
import PageHero from "../components/PageHero";
import CTASection from "../components/CTASection";

const Block = ({ title, body }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5 }}
  >
    <h2 className="font-heading text-2xl font-bold text-white mb-3">{title}</h2>
    <p className="text-slate-300 leading-relaxed">{body}</p>
  </motion.div>
);

const About = () => {
  const { t, lang } = useLanguage();
  const a = t.aboutPage;

  return (
    <div data-testid="about-page">
      <PageHero label={a.label} title={a.heading} image={IMG.engineer} />

      {/* Intro + counters */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-lg text-slate-200 leading-relaxed">{a.intro}</p>
            <a
              href="/TowerLink_Company_Profile.pdf"
              download="TowerLink_Platform_Profile.pdf"
              className="mt-7 inline-flex items-center gap-2 rounded-lg border border-[#D4AF37] px-5 py-3 text-sm font-semibold text-[#D4AF37] transition-colors hover:bg-[#D4AF37] hover:text-[#0A1223]"
              data-testid="platform-profile-download"
            >
              <Download size={18} />
              {lang === "ar" ? "تحميل بروفايل المنصة" : "Download Platform Profile"}
            </a>
            <div className="grid grid-cols-2 gap-6 mt-10">
              {counters.map((c) => (
                <div key={c.key} className="border-s-2 border-[#D4AF37]/40 ps-4">
                  <div className="font-heading text-3xl font-extrabold text-[#D4AF37]">
                    <Counter end={c.value} suffix={c.suffix} />
                  </div>
                  <p className="text-sm text-slate-300 mt-1">{t.trust[c.key]}</p>
                </div>
              ))}
            </div>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative"
          >
            <div className="absolute -inset-3 border border-[#D4AF37]/20 -z-10" />
            <img src={IMG.cityNight} alt="" className="w-full h-[440px] object-cover" />
          </motion.div>
        </div>
      </section>

      {/* What / Problem / How / Coverage */}
      <section className="py-16 md:py-24 bg-[#11203A] border-y border-white/5">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 grid sm:grid-cols-2 gap-x-12 gap-y-12">
          <Block title={a.whatHeading} body={a.whatBody} />
          <Block title={a.problemHeading} body={a.problemBody} />
          <Block title={a.howHeading} body={a.howBody} />
          <Block title={a.coverageHeading} body={a.coverageBody} />
        </div>
      </section>

      {/* Why TowerLink */}
      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass p-8 md:p-12 border-s-4 border-[#D4AF37]"
            data-testid="why-towerlink"
          >
            <h2 className="font-heading text-3xl font-bold text-white mb-5">{a.whyHeading}</h2>
            <p className="text-slate-200 leading-relaxed">{a.whyBody}</p>
            <p className="mt-8 font-heading text-xl md:text-2xl font-extrabold text-[#D4AF37] leading-snug">
              {a.whyTagline}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="pb-8">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 grid sm:grid-cols-2 gap-6">
          <div className="bg-[#11203A] border border-white/12 p-8" data-testid="mission-card">
            <div className="w-12 h-12 grid place-items-center border border-[#D4AF37]/30 text-[#D4AF37] mb-5">
              <Icon name="Target" size={22} />
            </div>
            <h3 className="font-heading text-xl font-bold text-white">{a.missionHeading}</h3>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">{a.missionBody}</p>
          </div>
          <div className="bg-[#11203A] border border-white/12 p-8" data-testid="vision-card">
            <div className="w-12 h-12 grid place-items-center border border-[#D4AF37]/30 text-[#D4AF37] mb-5">
              <Icon name="Eye" size={22} />
            </div>
            <h3 className="font-heading text-xl font-bold text-white">{a.visionHeading}</h3>
            <p className="mt-3 text-sm text-slate-300 leading-relaxed">{a.visionBody}</p>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white text-center mb-14">
            {a.differentHeading}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {pillars.map((p, i) => {
              const d = p[lang];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: (i % 3) * 0.08 }}
                  className="bg-[#0A1223] border border-white/12 p-8"
                  data-testid={`pillar-${i}`}
                >
                  <div className="w-12 h-12 grid place-items-center bg-[#D4AF37] text-[#0A1223] mb-5">
                    <Icon name={p.icon} size={22} />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-white">{d.title}</h3>
                  <p className="mt-3 text-sm text-slate-300 leading-relaxed">{d.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
      <CTASection />
    </div>
  );
};

export default About;
